import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useServerUrl from '../hooks/useServerUrl';
import './LocationForm.css';

interface Location {
  id: string;
  name: string;
  description?: string;
  locationKey: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface LocationFormProps {
  location?: Location | null;
  onClose: () => void;
  onSuccess: () => void;
}

const LocationForm: React.FC<LocationFormProps> = ({ location, onClose, onSuccess }) => {
  const { token } = useAuth();
  const SERVER_URL = useServerUrl(); // Runtime config
  const [formData, setFormData] = useState({
    name: location?.name || '',
    description: location?.description || '',
    isActive: location?.isActive !== undefined ? location.isActive : true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!location?.id;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = isEditing 
        ? `${SERVER_URL}/api/locations/${location.id}`
        : `${SERVER_URL}/api/locations`;
      
      const method = isEditing ? 'patch' : 'post';
      
      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} location`);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} location:`, err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="location-form-overlay">
      <div className="location-form">
        <div className="form-header">
          <h2>{isEditing ? 'Edit Location' : 'Create New Location'}</h2>
          <button onClick={onClose} className="close-btn">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">
              Location Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter location name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter location description (optional)"
              rows={4}
              disabled={loading}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkmark"></span>
              Active Location
            </label>
            <small className="checkbox-help">
              Active locations are available for camera management and monitoring
            </small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || !formData.name.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Location' : 'Create Location'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
